import React from "react";
import { Dropdown, Avatar, Button, Badge, Tag } from "antd";
import type { MenuProps } from "antd";
import {
    UserOutlined,
    IdcardOutlined,
    PoweroffOutlined,
    ApartmentOutlined,
    ThunderboltOutlined,
    UsergroupAddOutlined,
    BlockOutlined,
    BellOutlined,
    CommentOutlined,
    ControlOutlined,
    CrownOutlined,
    FundOutlined,
    TagOutlined,
    ToolOutlined,
    CaretDownOutlined,
    EnvironmentOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { useMaxWidth } from "../../hooks/useMaxWidth";
import { RootState } from "../../redux/store";
import { logout } from "../../redux/auth/auth.slice";
import { buildLoginPath } from "../../utils/returnPath";
import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";
import { RoleBadge } from "../PostCard/roleBadge";
import TruncateWithTooltip from "../Sidebar/TruncateWithTooltip";
import logo from "../../assets/P3M_logo.svg";

const ProfileDropdown: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMaxWidth(767);
    const isTablet = useMaxWidth(1024);

    // User and role states
    const user = useAppSelector((state: RootState) => state.auth.user);
    const isAuthenticated = useAppSelector((state: RootState) => state.auth.isAuthenticated);
    const admin = useAppSelector((state: RootState) => state.auth.adminMode);
    const owner = useAppSelector((state: RootState) => state.auth.ownerMode);
    const superAdmin = useAppSelector((state: RootState) => state.auth.superAdminMode);
    const p3mSuperAdmin = useAppSelector((state: RootState) => state.auth.p3mSuperAdminMode);
    const auditor = useAppSelector((state: RootState) =>
        state.auth.user?.role?.includes("Auditor")
    );
    const isEvaluator = useAppSelector((state: RootState) =>
        state.auth.user?.role?.includes("Evaluator")
    );

    // Check if user is using an invitation link
    const hasInvitationToken = localStorage.getItem('invitation_token') || localStorage.getItem('anonymous_invitation_token');
    const invitationData = (() => {
        try {
            const data = sessionStorage.getItem('invitationData');
            if (data) {
                return JSON.parse(data);
            }
        } catch (e) {
            // Ignore
        }
        return null;
    })();

    // Check if user is anonymous/guest (but not if they have valid invitation data)
    const isAnonymous = !isAuthenticated || (user && user.isAnonymous);
    // Invited user: has invitation token/data and is not authenticated (anonymous invitation)
    const isInvitedUser = hasInvitationToken && invitationData && !isAuthenticated;
    // Authenticated user with invitation: has invitation data but is authenticated

    // Get user display name - prioritize invitation name for invited users
    const displayName = isInvitedUser
        ? (invitationData.inviteeName || invitationData.name || user?.name || 'Invited User')
        : isAnonymous
            ? (invitationData?.inviteeName || invitationData?.name || user?.name || 'Guest User')
            : (user?.name || 'User');

    // Organization data
    const selectedOrganization = useAppSelector((state) => state.organization.selectedOrganization);

    // Notifications data
    const notificationSummary = useAppSelector(
        (state: RootState) => state.notifications.summary
    );
    const unreadNotificationCount = notificationSummary.totalUnread;

    const handleLogout = () => {
        dispatch(logout());
        navigate(buildLoginPath(location, { owner }));
        minimalToast({
            dispatch,
            title: "Success",
            message: "User has been logged out Successfully!!",
            type: NotificationSeverity.SUCCESS,
            category: NotificationCategory.ALERT,
            priority: NotificationPriority.HIGH,
        });
    };

    const handleProfileClick = () => {
        navigate(`/profile/${user?._id}/${user?.name}`);
    };

    // Build menu items based on user role and permissions
    const getMenuItems = (): MenuProps['items'] => {
        const items: MenuProps['items'] = [
            // Profile Section
            {
                key: 'profile-info',
                type: 'group',
                label: (
                    <div className="profile-dropdown-header">
                        {isMobile && (
                            <div className="profile-dropdown-logo">
                                <img src={logo} alt="P3M Logo" className="mobile-dropdown-logo" />
                            </div>
                        )}
                        <div className="profile-dropdown-user-section">
                            <Avatar
                                size={isMobile ? 32 : 40}
                                src={user?.picture}
                                icon={!user?.picture && <UserOutlined />}
                                className="profile-dropdown-avatar"
                            />
                            <div className="profile-dropdown-info">
                                <div className="profile-dropdown-name">
                                    <TruncateWithTooltip maxWidth={isMobile ? 120 : 160}>
                                        {displayName}
                                    </TruncateWithTooltip>
                                    {(isInvitedUser || (isAnonymous && invitationData)) && invitationData?.role && (
                                        <Tag color="blue" style={{ marginLeft: 4 }}>
                                            {invitationData.role}
                                        </Tag>
                                    )}
                                    {!isInvitedUser && isAnonymous && !invitationData && (
                                        <Tag color="orange" style={{ marginLeft: 4 }}>
                                            Guest User
                                        </Tag>
                                    )}
                                    {!isAnonymous && user?.jobTitle && (
                                        <Tag color="blue" style={{ marginLeft: 4 }}>
                                            {user.jobTitle}
                                        </Tag>
                                    )}
                                    {!isAnonymous && (
                                        <span className="profile-dropdown-badge">
                                            {RoleBadge({
                                                p3mSuperAdmin: p3mSuperAdmin,
                                                superAdmin: superAdmin,
                                                admin: admin,
                                                owner: owner
                                            })}
                                        </span>
                                    )}
                                </div>
                                <div className="profile-dropdown-role">
                                    {isInvitedUser || (isAnonymous && invitationData)
                                        ? (invitationData?.role || "Auditor")
                                        : isAnonymous
                                            ? "Anonymous Auditor (Guest)"
                                            : p3mSuperAdmin
                                                ? "P3M Super Admin"
                                                : superAdmin
                                                    ? "Super Admin"
                                                    : admin
                                                        ? "Admin"
                                                        : owner
                                                            ? "Owner"
                                                            : auditor
                                                                ? "Auditor"
                                                                : isEvaluator
                                                                    ? "Evaluator"
                                                                    : "Auditee"}
                                </div>
                                {!isAnonymous && user?.jobTitle && (
                                    <div className="profile-dropdown-job-title">
                                        {user.jobTitle}
                                    </div>
                                )}
                                {selectedOrganization && (
                                    <div className="profile-dropdown-org">
                                        <EnvironmentOutlined style={{ fontSize: 9, marginRight: 5 }} />
                                        <TruncateWithTooltip maxWidth={isMobile ? 110 : 150}>
                                            {selectedOrganization.name}
                                        </TruncateWithTooltip>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                type: 'divider',
            },
            // Profile Management
            {
                key: 'profile',
                label: 'My Profile',
                icon: <IdcardOutlined />,
                onClick: handleProfileClick,
            },
        ];

        // Mobile-only items (notifications and messages)
        if (isMobile) {
            items.push(
                {
                    key: 'notifications',
                    label: (
                        <Badge count={unreadNotificationCount} size="small">
                            <span>Notifications</span>
                        </Badge>
                    ),
                    icon: <BellOutlined />,
                    onClick: () => navigate('/notifications'),
                },
                {
                    key: 'messages',
                    label: 'Messages',
                    icon: <CommentOutlined />,
                    onClick: () => {
                        // You can implement message navigation here
                        console.log('Navigate to messages');
                    },
                },
                {
                    type: 'divider',
                }
            );
        }

        // Organization Management (for admins and owners)
        if (admin || owner || superAdmin || p3mSuperAdmin) {
            items.push({
                key: 'org-settings',
                label: 'Organization Settings',
                icon: <ApartmentOutlined />,
                children: [
                    ...(owner || superAdmin || p3mSuperAdmin ? [{
                        key: 'manage-org',
                        label: 'Manage Organization',
                        icon: <ControlOutlined />,
                        onClick: () => navigate('/add-organization'),
                    }] : []),
                    {
                        key: 'audit-categories',
                        label: 'Audit Categories',
                        icon: <BlockOutlined />,
                        onClick: () => navigate('/audit-categories'),
                    },
                    ...(admin || superAdmin || auditor || p3mSuperAdmin ? [{
                        key: 'parameter-settings',
                        label: 'Parameter Settings',
                        icon: <ToolOutlined />,
                        onClick: () => navigate('/parameter-settings'),
                    }] : []),
                ],
            });
        }

        // Admin Features (for evaluators with specific permissions)
        if (isEvaluator && user?.adminFeaturesAllowed?.length) {
            const evaluatorItems = [];

            if (user.adminFeaturesAllowed.includes("ManageAuditee")) {
                evaluatorItems.push({
                    key: 'manage-auditee',
                    label: 'Manage Auditee',
                    icon: <UsergroupAddOutlined />,
                    onClick: () => navigate('/all-employees'),
                });
            }

            if (evaluatorItems.length > 0) {
                items.push({
                    key: 'admin-features',
                    label: 'Admin Features',
                    icon: <CrownOutlined />,
                    children: evaluatorItems,
                });
            }
        }

        // P3M Super Admin Features
        if (p3mSuperAdmin) {
            items.push({
                key: 'super-admin',
                label: 'Super Admin',
                icon: <ThunderboltOutlined />,
                children: [
                    {
                        key: 'ai-analytics',
                        label: 'AI Analytics',
                        icon: <FundOutlined />,
                        onClick: () => navigate('/ai-analytics'),
                    },
                    {
                        key: 'coupon-manager',
                        label: 'Coupon Manager',
                        icon: <TagOutlined />,
                        onClick: () => navigate('/coupon-manager'),
                    },
                ],
            });
        }

        // Divider before logout
        items.push({
            type: 'divider',
        });

        // Logout
        items.push({
            key: 'logout',
            label: (
                <span className="logout-item">
                    <PoweroffOutlined />
                    <span>Logout</span>
                </span>
            ),
            onClick: handleLogout,
            className: 'logout-menu-item',
        });

        return items;
    };

    return (
        <Dropdown
            menu={{ items: getMenuItems() }}
            placement="bottomRight"
            trigger={['click']}
            overlayClassName="profile-dropdown-overlay"
            arrow={{ pointAtCenter: true }}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
        >
            <Button
                type="text"
                className="profile-dropdown-trigger"
                size={isMobile ? "small" : "middle"}
            >
                <Avatar
                    size={isMobile ? 26 : 28}
                    src={user?.picture}
                    icon={!user?.picture && <UserOutlined />}
                    className="profile-dropdown-trigger-avatar"
                />
                {!isMobile && (
                    <span className="profile-dropdown-trigger-name">
                        <TruncateWithTooltip maxWidth={isTablet ? 100 : 120}>
                            {displayName}
                        </TruncateWithTooltip>
                        {!isAnonymous && (
                            <span className="profile-dropdown-trigger-badge">
                                {RoleBadge({
                                    p3mSuperAdmin,
                                    superAdmin,
                                    admin,
                                    owner,
                                })}
                            </span>
                        )}
                    </span>
                )}
                {!isMobile && (
                    <CaretDownOutlined className="profile-dropdown-trigger-caret" />
                )}
            </Button>
        </Dropdown>
    );
};

export default ProfileDropdown;
